<template>
  <div class="file-drop">
    <switch-view-mode
      v-if="!disableModeSwitch"
      class="file-drop__switch"
      v-bind:value="selectedViewMode"
      v-on:input="switchViewMode"
    />
    <div class="file-drop__files">
      <ul
        v-if="files.length > 0"
        class="file-drop__file-column">
        <li
          v-for="(file, index) in files"
          v-bind:key="index">
          <div
            class="file-drop__row"
            v-bind:class="{ error: isErrorFile(file) }">
            <button
              class="file-drop__image"
              type="button"
              v-on:click="openFile(file)">{{ file.name }}</button>
            <div class="file-drop__group-button">
              <button
                class="file-drop__download"
                type="button"
                title="Скачать снимок"
                v-on:click.stop="downloadFile(file)" />
              <button
                class="file-drop__delete"
                type="button"
                title="Удалить снимок"
                v-on:click.stop="removeFile(file)" />
            </div>
            <div
              v-if="getFileFieldDetails(file).length > 0"
              class="file-drop__error">
              {{ getFileFieldDetails(file)[0] }}
            </div>
          </div>
        </li>
      </ul>
    </div>
    <div class="file-drop__snapshots">
      <ul
        v-if="snapshots.length > 0"
        class="file-drop__snapshot-column">
        <li
          v-for="(photo, index) in snapshots"
          v-bind:key="index">
          <patient-snapshot
            class="file-drop__snapshot-column__snapshot"
            v-bind:file="photo"
            v-bind:size="'small'"
            v-on:delete-snapshot="removeFile"
            v-on:show-snapshot="openFile"
            v-on:download-snapshot="downloadFile"
          />
        </li>
      </ul>
    </div>
    <div
      class="file-drop__upload"
      v-on:drop="dropFiles"
      v-on:dragover.prevent>
      <button
        class="file-drop__container"
        type="button"
        v-on:click="fileInput.click()">
        <img
          alt="Добавьте файлы"
          class="patient-visits__files__icon-add"
          src="@/assets/img/add-files.svg"
          width="51"
          height="46" />
        <p>
          Перетащите файлы в эту область или нажмите сюда
          <template v-if="hint">({{ hint }})</template>
        </p>
      </button>
    </div>
    <div
      v-if="storageInputError.length > 0"
      class="file-drop__error">
      {{ storageInputError.join("\n ") }}
    </div>
  </div>
</template>
<script>

import PatientSnapshot from '@/components/PatientSnapshot.vue';
import SwitchViewMode, { TABLE_MODE } from '@/components/SwitchViewMode.vue';

const imageTypes = [
  'image/png',
  'image/jpeg',
  'image/jpg',
];

const acceptedList = [
  ...imageTypes,
  'application/pdf',
  'application/msword',
  'application/vnd.msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  '.doc',
  '.docx',
  '.jpeg',
  '.jpg',
  '.png',
];

/**
 * @description Разрешить получение списка ошибок для поля ввода.
 * @param {string} cause Имя поля ввода.
 * @returns {function}
 */
const resolveInputErrors = (cause) => (vm) => (
  cause in vm.violationCauses ? vm.violationCauses[cause] : []
);

/**
 * @description Компонент загрузки файлов.
 * @type {any}
 */
export default {
  components: {
    SwitchViewMode,
    PatientSnapshot,
  },
  computed: {
    storageInputError: resolveInputErrors('storage'),
  },
  data() {
    return {
      /**
       * @description Выбранный режим отображения.
       * @type {String}
       */
      selectedViewMode: this.initialViewMode,
      /**
       * @description Поле файла.
       * @type {any}
       */
      fileInput: {},
      /**
       * @description Файлы.
       * @type {any[]}
       */
      files: [],
      /**
       * @description Снимки.
       * @type {any[]}
       */
      snapshots: [],
    };
  },
  methods: {
    /**
     * @description Переключить режим отображения.
     * @param mode
     * @returns {void}
     */
    switchViewMode(mode) {
      this.selectedViewMode = mode;
    },
    /**
     * @description Добавить файлы для загрузки.
     * @param {FileList} files
     */
    addFiles(files) {
      for (let i = 0; i < files.length; i += 1) {
        if (!files[i].type || this.acceptedList.includes(files[i].type)) {
          const url = URL.createObjectURL(files[i]);
          const file = files[i];
          file.url = url;
          this.$emit('upload-file', files[i]);
        }
      }
    },
    /**
     * @description Отправить событие на скачивание файла.
     * @param {any} file
     * @returns {void}
     */
    downloadFile(file) {
      this.$emit('download-file', file);
    },
    /**
     * @description Отправить событие на предпросмотр файла.
     * @param {any} file
     * @returns {void}
     */
    openFile(file) {
      this.$emit('open-file', file);
    },
    isErrorFile(file) {
      return this.errorFiles.includes(file);
    },
    /**
     * @description Обработчик перетаскивания файлов.
     * @param {Event} ev
     * @returns {void}
     */
    dropFiles(ev) {
      ev.preventDefault();
      if (ev.dataTransfer.files) {
        this.addFiles(ev.dataTransfer.files);
      }
    },
    getFileFieldDetails(file) {
      const { name } = file;
      return this.violationCauses[`file_${name}`] || [];
    },
    /**
     * @description Удалить файл.
     * @param {any} file
     * @returns {void}
     */
    removeFile(file) {
      this.$emit('remove-file', file);
    },
    /**
     * @description Установить текущие файлы.
     * @param {any} files Список файлов.
     * @returns {void}
     */
    setFiles(files) {
      if (!files) {
        return;
      }
      this.files = files.filter((el) => !imageTypes.includes(el.type));
      this.snapshots = files.filter((el) => imageTypes.includes(el.type));
    },
  },
  mounted() {
    this.fileInput = document.createElement('input');
    this.fileInput.type = 'file';
    this.fileInput.accept = this.acceptedList.join(',');
    this.fileInput.multiple = true;
    this.fileInput.onchange = (event) => {
      const { files } = event.target;
      this.addFiles(files);
    };
  },
  props: {
    /**
     * @description Выключить возможность переключать режимы.
     * @type {Boolean}
     */
    disableModeSwitch: {
      required: false,
      type: Boolean,
      default: () => true,
    },
    /**
     * @description Начальное значение режима отображения.
     * @type {String}
     */
    initialViewMode: {
      required: false,
      type: String,
      default: () => TABLE_MODE,
    },
    acceptedList: { type: Array, default: () => acceptedList },
    errorFiles: { type: Array, required: true },
    hasSavePatient: { type: Boolean },
    inputFiles: { type: Array, required: true },
    violationCauses: { type: Object, required: true },
    hint: { type: String, default: () => 'не более 24 Мб' },
  },
  watch: {
    inputFiles: {
      handler: 'setFiles',
      immediate: true,
    },
  },
};
</script>
<style lang="scss" scoped>
@use "@/scss/buttons";
.file-drop {
  &__upload {
    margin-top: 15px;
    cursor: pointer;
    display: flex;
    flex-direction: column;
    width: 100%;
    border: 2px dashed #c2cfe0;
    border-radius: 2px;
    padding: 32px;
    align-items: center;
    box-sizing: border-box;
    width: 100%;
  }
  &__switch {
    padding: 20px 0 20px 0;
  }
  &__files {
    margin-bottom: 25px;
  }
  &__snapshots {
    margin-top: 25px;
    overflow-y: auto;
  }
  &__snapshot-column {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(155px, 178px));
    gap: 15px 25px;
    max-height: 615px;
    padding-left: 0 !important;
    list-style: none;
    &__snapshot {
      width: 150px;
      height: 175px;
    }
  }
  &__file-column {
    align-self: flex-start;
    display: grid;
    align-items: flex-start;
    grid-template-columns: 1fr;
    gap: 10px;
    list-style: none;
    padding-left: 0 !important;
  }
  &__row {
    display: grid;
    grid-template-columns: 1fr auto;
    gap: 5px 20px;
    align-items: center;
    transition: background-color var(--animate-duration);
    cursor: pointer;
    padding: 0 4px;
    &:hover {
      background-color: var(--section-background-color);
    }
  }
  &__group-button {
    display: flex;
    align-items: center;
    column-gap: 8px;
  }
  &__image {
    text-align: left;
    height: 100%;
  }
  &__delete {
    @include buttons.btn-cross-remove(16px, 16px);
    flex-shrink: 0;
    background-size: 12px 12px;
  }
  &__download {
    @include buttons.btn-download(16px, 16px);
    flex-shrink: 0;
  }
  &__error {
    color: #ff5252;
    padding: 5px;
    white-space: pre-wrap;
  }
  &__container {
    display: flex;
    flex-direction: column;
    width: 100%;
    align-items: center;
    p {
      margin: 18px 0 0 0;
      max-width: 395px;
      color: #90a0b7;
      font-weight: 500;
      font-size: 16px;
      line-height: 19px;
      text-align: center;
    }
  }
}

@media screen and (max-width: 480px) {
  .file-drop {
    &__snapshot-column {
      grid-template-columns: repeat(2, minmax(100px, 480px));
      justify-content: center;
      justify-items: center;
      gap: 25px 15px;
      max-height: 300px;
    }
  }
}
</style>
